import React from 'react';
import type { PreloadedHydrateJqlBuilderQuery } from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/useHydrationQueryLoader.tsx';
import type { Attributes } from '@atlassian/jira-product-analytics-bridge';
import type { JQLBuilderUI$data } from '@atlassian/jira-jql-builder/src/ui/__generated__/JQLBuilderUI.graphql';
import type { AdvancedContent$data } from '@atlassian/jira-filter-refinement/src/ui/filter-popup/filter-popup-content/ui/advanced/__generated__/AdvancedContent.graphql';
import type { JiraProjectOptions } from './ui/jql-editor/field/picker-wrapper/props-resolver/project/__generated__/getProjectProps_recentlyUsedProjectsQuery.graphql';
import type { AddFilterButtonComponent, NoOptionComponentType } from './common/types.tsx';
import type { JQLEditorUIProps } from './common/ui/types.tsx';
import JQLBuilderBasicUI, {
	JQLBuilderBasicUIWithAnalyticsOld as JQLBuilderBasicUIOld,
} from './ui/index.tsx';
import JQLEditorUI, {
	JQLEditorUIWithErrorBoundaryOld as JQLEditorUIOld,
} from './ui/jql-editor/JQLEditorUIWithErrorBoundary.tsx';

export type JQLBuilderBasicOldProps = {
	query: string;
	excludedFields?: string[];
	onSearch?: (jql: string) => void;
	onSwitchToJql?: () => void;
	onReady?: () => void;
	onLoad?: () => void;
	/**
	 * Event emitted when the user initiates a search journey in the JQL builder. A search journey is started when the
	 * user opens a field picker or focuses the text search input.
	 *
	 * @returns {Attributes} Analytics attributes to associate with the interaction that triggered the search journey.
	 */
	onJourneyStart?: () => Attributes;
	NoOptionComponent?: NoOptionComponentType;
	projectOptions?: JiraProjectOptions;
	singleSelectFields?: string[];
	hydrationQueryRef: PreloadedHydrateJqlBuilderQuery;
	isHydrating: boolean;
	/**
	 * Renders basic JQL Editor items as list items which drastically improves accessibility,
	 * as it's much easier to navigate between numerous filter buttons when they're part of a list.
	 *
	 * Don't forget to wrap with a <ul>!
	 */
	shouldUseListMarkup?: boolean;

	/**
	 * Hide operator dropdown allow you to select which operator to use for each field
	 * @default false
	 */
	hideOperatorDropdown?: boolean;
	irremovableFields: JQLEditorUIProps['irremovableFields'];

	/**
	 * Hide the text search input in the JQL Builder basic mode.
	 *
	 * @default false
	 */
	hideTextSearchInput?: boolean;
	/**
	 * Used to set a custom placeholder text for the search input.
	 * Note - Ensure the placeholder text is unique, as it also used as aria-label for screen reader
	 */
	searchPlaceholder?: string;
	/**
	 * Displays the orderBy picker for configuring the sorting behaviour of the search in the JQL Builder basic mode.
	 *
	 * @default false
	 */
	showOrderByPicker?: boolean;

	/**
	 * Context JQL is the jql used by Graphql queries to return results based on that context
	 * eg: will return fields / issuetypes / statuses specific to a project if that project is in the context jql
	 */
	contextJql: string;
	/**
	 * Instructs the backend to return fields corresponds to the view
	 */
	viewContext?: JQLEditorUIProps['viewContext'];
	/**
	 * Scope allows consumers to customise the fields returned by JQL filter. For example, sprint field could
	 * be conditionally removed when a boardId scope is provided.
	 */
	scope?: JQLEditorUIProps['scope'];

	/**
	 * If true, the JQL Builder will be rendered in vertical mode.
	 * @default false
	 */
	verticalMode?: boolean;

	/**
	 * If true, the JQL Builder popups will be rendered to the parent element.
	 * @default true
	 */
	shouldRenderToParent?: boolean;
};

export type JQLBuilderBasicProps = {
	query: string;
	excludedFields?: string[];
	onSearch?: (jql: string) => void;
	onSwitchToJql?: () => void;
	onReady?: () => void;
	onLoad?: () => void;
	/**
	 * Event emitted when the user initiates a search journey in the JQL builder. A search journey is started when the
	 * user opens a field picker or focuses the text search input.
	 *
	 * @returns {Attributes} Analytics attributes to associate with the interaction that triggered the search journey.
	 */
	onJourneyStart?: () => Attributes;
	NoOptionComponent?: NoOptionComponentType;
	AddFilterButtonComponent?: AddFilterButtonComponent;
	projectOptions?: JiraProjectOptions;
	singleSelectFields?: string[];
	hydrationData: JQLBuilderUI$data | AdvancedContent$data;
	isHydrating: boolean;
	/**
	 * Renders basic JQL Editor items as list items which drastically improves accessibility,
	 * as it's much easier to navigate between numerous filter buttons when they're part of a list.
	 *
	 * Don't forget to wrap with a <ul>!
	 */
	shouldUseListMarkup?: boolean;

	/**
	 * Hide operator dropdown allow you to select which operator to use for each field
	 * @default false
	 */
	hideOperatorDropdown?: boolean;
	irremovableFields: JQLEditorUIProps['irremovableFields'];

	/**
	 * Hide the text search input in the JQL Builder basic mode.
	 *
	 * @default false
	 */
	hideTextSearchInput?: boolean;
	/**
	 * Used to set a custom placeholder text for the search input.
	 * Note - Ensure the placeholder text is unique, as it also used as aria-label for screen reader
	 */
	searchPlaceholder?: string;
	/**
	 * Displays the orderBy picker for configuring the sorting behaviour of the search in the JQL Builder basic mode.
	 *
	 * @default false
	 */
	showOrderByPicker?: boolean;

	/**
	 * Context JQL is the jql used by Graphql queries to return results based on that context
	 * eg: will return fields / issuetypes / statuses specific to a project if that project is in the context jql
	 */
	contextJql: string;
	/**
	 * Instructs the backend to return fields corresponds to the view.
	 */
	viewContext?: JQLEditorUIProps['viewContext'];
	/**
	 * Scope allows consumers to customise the fields returned by JQL filter. For example, sprint field could
	 * be conditionally removed when a boardId scope is provided.
	 */
	scope?: JQLEditorUIProps['scope'];

	/**
	 * If true, the JQL Builder will be rendered in vertical mode.
	 * @default false
	 */
	verticalMode?: boolean;

	/**
	 * If true, the JQL Builder popups will be rendered to the parent element.
	 * @default true
	 */
	shouldRenderToParent?: boolean;

	/**
	 * Func for the clear all button.
	 */
	onReset?: () => void;
};

export const JQLBuilderBasicOld = (props: JQLBuilderBasicOldProps): React.JSX.Element => (
	<JQLBuilderBasicUIOld {...props} JQLEditor={JQLEditorUIOld} />
);

const JQLBuilderBasic = (props: JQLBuilderBasicProps): React.JSX.Element => (
	<JQLBuilderBasicUI {...props} JQLEditor={JQLEditorUI} />
);

export default JQLBuilderBasic;
